import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ErrorType } from '../../../../../../../domain/entities/Auth';
import {
  LoginQuery,
  LoginWithFbQuery,
} from '../../../../../../../hooks/api/auth';
import FACEBOOK_API_ID from '../../../../../../../utils/facebook-api-id';
import TextInput, { TextInputType } from '../../../../../../../atoms/TextInput';
import LabeledContainer from '../../../../../../../molecules/common/LabeledContainer';
import ErrorMessage from '../LoginErrorMessage';
import { PasswordForgot } from '../../../../../../../molecules/common/PasswordForgot';
import { SubmitButton } from '../../../../../../../atoms/SubmitButton';

interface Props {
  postable: boolean;
  onLogin: (query: LoginQuery) => void;
  onLoginWithFb: (query: LoginWithFbQuery) => void;
  errors?: ErrorType[];
}

interface ResponseFacebookMe {
  email: string;
  first_name: string;
  id: string;
  last_name: string;
}

declare global {
  interface Window {
    fbAsyncInit: () => void;
  }
}

const InputLogin: React.FC<Props> = (props) => {
  const intl = useIntl();

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  const submittable = useMemo(() => {
    return !!email && !!password;
  }, [email, password]);

  useEffect(() => {
    loadFbLoginApi();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFbLoginApi = () => {
    window.fbAsyncInit = () => {
      FB.init({
        appId: FACEBOOK_API_ID,
        cookie: true,
        xfbml: true,
        version: 'v4.0',
      });
      FB.AppEvents.logPageView();
    };
    ((d) => {
      const fjs = d.getElementsByTagName('script')[0];
      if (d.getElementById('facebook-jssdk')) {
        return;
      }
      const js = d.createElement('script');
      js.id = 'facebook-jssdk';
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode!.insertBefore(js, fjs);
    })(document);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      currentTarget: { name, value },
    } = e;
    if (name === 'email') setEmail(value);
    if (name === 'password') setPassword(value);
  };

  const handleSubmit = useCallback(async () => {
    props.onLogin({ email, password });
  }, [email, password, props]);

  const handleFacebookSubmit = async () => {
    FB.login(
      (resLogin) => {
        if (resLogin.authResponse) {
          FB.api(
            '/me?fields=email,first_name,last_name',
            (resMe: ResponseFacebookMe) => {
              const query = {
                email: resMe.email,
                id: resMe.id,
                accessToken: resLogin.authResponse.accessToken,
                firstName: resMe.first_name,
                lastName: resMe.last_name,
                expireAt: resLogin.authResponse.expiresIn,
              };
              props.onLoginWithFb(query);
            },
          );
        }
      },
      { scope: 'email' },
    );
  };

  const doYouForgetPassword = intl.formatMessage({
    id: 'auth.login.links.forgotPassword',
  });

  return (
    <div>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'purchases.inputLogin.email',
        })}
        paddingTop={true}
      >
        <TextInput
          type={TextInputType.Email}
          name={'email'}
          value={email}
          onChange={handleChange}
        />
      </LabeledContainer>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'purchases.inputLogin.password',
        })}
      >
        <TextInput
          type={TextInputType.Password}
          name={'password'}
          value={password}
          onChange={handleChange}
        />
      </LabeledContainer>
      <ErrorMessage errors={props.errors} />
      <PasswordForgot message={doYouForgetPassword} />
      <SubmitButton
        type="button"
        onClick={handleSubmit}
        value={intl.formatMessage({ id: 'purchases.inputLogin.submitLogin' })}
        disabled={!submittable || !props.postable}
        postable={props.postable}
      >
        {intl.formatMessage({ id: 'purchases.inputLogin.submitLogin' })}
      </SubmitButton>
      <SubmitButton
        type="button"
        onClick={handleFacebookSubmit}
        value={intl.formatMessage({
          id: 'purchases.inputLogin.submitFacebookLogin',
        })}
        disabled={!props.postable}
        postable={props.postable}
        variant="Secondary"
      >
        {intl.formatMessage({
          id: 'purchases.inputLogin.submitFacebookLogin',
        })}
      </SubmitButton>
    </div>
  );
};

export default InputLogin;
